import * as React from 'react';
import { Topbar } from './topbar';

interface LayoutProps {
  children: React.ReactNode;
  sidebar: React.ReactNode;
}

export function Layout({ children, sidebar }: LayoutProps) {
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  // Clone the sidebar element to inject mobileOpen and a close-on-navigate callback,
  // only when it is a non-fragment React element. Otherwise, render it unchanged.
  let enhancedSidebar = sidebar;

  if (React.isValidElement(sidebar) && sidebar.type !== React.Fragment) {
    const sidebarEl = sidebar as React.ReactElement<{
      mobileOpen?: boolean;
      onNavigationClick?: () => void;
    }>;
    const originalOnNavClick = sidebarEl.props?.onNavigationClick;
    enhancedSidebar = React.cloneElement(sidebarEl, {
      mobileOpen: mobileNavOpen,
      onNavigationClick: () => {
        originalOnNavClick?.();
        setMobileNavOpen(false);
      },
    });
  }

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded"
      >
        Skip to main content
      </a>

      {/* Mobile backdrop — closes drawer when tapped */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          aria-hidden="true"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      {enhancedSidebar}

      <div className="flex flex-1 flex-col overflow-hidden">
        <Topbar onOpenMobileNav={() => setMobileNavOpen(true)} />
        <main id="main-content" role="main" tabIndex={-1} className="flex-1 overflow-auto outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background">
          {children}
        </main>
      </div>
    </div>
  );
}
